import { ReactNode, useCallback } from 'react';

import { MedicalDeviceOrderLastUpdatedQuery } from '@dreryk/egabinet-ezwm-frontend-api';

import { Translate } from '@dreryk/react-common';
import {
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
   useHistoryPushWithBackAction,
} from '@dreryk/react-components';
import { performCqrsQueryFind } from '@dreryk/command-query-react-common';

import { protectFeature } from '~libs.shared.system';
import { visitPaths } from '~libs.desktop.shared.paths';

import { medicalDevicesTranslationKeys } from '../../../../features/medicalDevices/translations';

type CheckExistingEzwmDraftsShowModalEventInterceptorProps = {
   readonly visitId: number;
   readonly children: ReactNode;
};

export const CheckExistingEzwmDraftsShowModalEventInterceptor = (
   props: CheckExistingEzwmDraftsShowModalEventInterceptorProps
): JSX.Element => {
   const push = useHistoryPushWithBackAction(true);
   const goToMedicalDevices = useCallback(
      (): void =>
         push(visitPaths.getMedicalDevicesPath({ params: { visitId: props.visitId } }), {
            label: medicalDevicesTranslationKeys.goBackToVisit,
         }),
      [push, props.visitId]
   );

   const handleShowModal = useCallback(async (): Promise<boolean> => {
      const medicalDeviceOrderCheckNecessary = protectFeature('issuingMedicalDeviceOrders');
      if (medicalDeviceOrderCheckNecessary) {
         return false;
      }

      try {
         const lastUpdatedMedicalDeviceOrder = await performCqrsQueryFind<boolean>(() => {
            const medicalDeviceOrderQuery = new MedicalDeviceOrderLastUpdatedQuery();
            medicalDeviceOrderQuery.visitId = props.visitId;
            return medicalDeviceOrderQuery;
         });

         return Boolean(lastUpdatedMedicalDeviceOrder);
      } catch (__) {
         // Ignore fetch errors in case f.e. ezwm microservice is down (expected behaviour)
         return false;
      }
   }, [props.visitId]);

   return (
      <ShowModalEventInterceptor
         testId={'checkExistingMedicalDeviceOrderDrafts'}
         shouldShowModal={handleShowModal}
         title={medicalDevicesTranslationKeys.draftFormExistsTitle}
         // eslint-disable-next-line
         content={({ resolve, reject }) => {
            return (
               <>
                  <ModalConfirmButton onClick={resolve} label={medicalDevicesTranslationKeys.confirm} />
                  <ModalCancelButton
                     onClick={goToMedicalDevices}
                     label={medicalDevicesTranslationKeys.goBackToMedicalDeviceOrder}
                  />
                  <ModalCloseButton onClick={reject} />
                  <Translate>{medicalDevicesTranslationKeys.draftFormExistsMessage}</Translate>
               </>
            );
         }}
         modalWidth={ModalWidth.NORMAL}
         footerItemsHidden
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
